/** @todo Fix import cycle */
// eslint-disable-next-line import/no-cycle
import { getErrorMessage } from '../utils/helpers'
import axios from './instance'

/**
 * Gets the list of clients the user has access to.
 * @param {string} url
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClients = async (
  url,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(url)

    setLoading(false)
    setSuccess(data)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Deletes a client.
 * @param {string} id
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 */

export const deleteClient = async (
  id,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    await axios.delete(`/clients/${id}/`)
    setLoading(false)
    setSuccess(true)
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
  }
}

/**
 * Get the client by id.
 * @param {string} id
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClient = async (
  id,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(
      `/clients/${id}/?expand=owner,project_manager,resource,pending_data_import,latest_successful_import,latest_qc_check,is_pending_initial_data_upload`,
    )

    setLoading(false)
    setSuccess(true)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Updates the client with the specified `payload`.
 * @param {object} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const updateClient = async (
  payload,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.patch(
      `/clients/${payload.id}/?expand=owner,project_manager,resource,pending_data_import,latest_successful_import,latest_qc_check,is_pending_initial_data_upload`,
      payload,
    )

    setLoading(false)
    setSuccess(true)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Updates the client with the specified `payload`.
 * @param {str} clientId
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const resetClientData = async (
  clientId,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/clients/${clientId}/reset_data/`)

    setLoading(false)
    setSuccess('Data reset scheduled successfully. This may take some time to complete.')
    return data
  } catch (err) {
    setError(getErrorMessage(err) || 'Failed to schedule data reset. Please try again later.')
    setLoading(false)
    return null
  }
}

/** --------------------------- Client: Related --------------------------- */

/**
 * Get the composites for the specified client by `id`.
 * @param {string} id
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClientPortfolioDataComposites = async (
  id,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(`/clients/${id}/composites?limit=99999`) // Set high limit to prevent pagination

    setLoading(false)
    return data.results
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get the accounts for the specified client by `id`.
 * @param {string} id
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClientPortfolioDataAccounts = async (
  id,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(`/clients/${id}/accounts?limit=99999`) // Set high limit to prevent pagination

    setLoading(false)
    return data.results
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get the portfolio periods for the specified client by `id`.
 * @param {string} id
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClientPortfolioDataPeriods = async (
  id,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(`/clients/${id}/portfolio_periods`)

    setLoading(false)
    return data.periods
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get the pending portfolio periods for the specified client by `id`.
 * @param {string} id
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClientPendingPortfolioDataPeriods = async (
  id,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(`/clients/${id}/pending_portfolio_periods`)

    setLoading(false)
    return data.periods
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get the portfolios for the specified client by `id`.
 * @param {string} id
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClientPortfolioDataPortfolios = async (
  id,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(`/clients/${id}/portfolios?limit=99999`) // Set high limit to prevent pagination

    setLoading(false)
    return data.results
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get the years for the specified client by `id`.
 * @param {string} id
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClientAumDataYears = async (id, setError = () => {}, setLoading = () => {}) => {
  setLoading(true)

  try {
    const data = await axios.get(`/clients/${id}/years`)

    setLoading(false)
    return data.years
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get the benchmark periods for the specified client by `id`.
 * @param {string} id
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClientBenchmarkDataPeriods = async (
  id,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(`/clients/${id}/benchmark_periods`)

    setLoading(false)
    return data.years
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get the owners for all clients.
 * @param {func} setError
 * @returns `data` results
 */
export const getOwners = async (setError = () => {}) => {
  try {
    const data = await axios.get(`/clients/owners`)

    return data.owners
  } catch (err) {
    setError(getErrorMessage(err))
    return null
  }
}

/**
 * Get the project managers for all clients.
 * @param {func} setError
 * @returns `data` results
 */
export const getProjectManagers = async (setError = () => {}) => {
  try {
    const data = await axios.get(`/clients/project_managers`)
    return data.projectManagers
  } catch (err) {
    setError(getErrorMessage(err))
    return null
  }
}

/**
 * Get the resources for all clients.
 * @param {func} setError
 * @returns `data` results
 */
export const getResources = async (setError = () => {}) => {
  try {
    const data = await axios.get(`/clients/resources`)
    return data.resources
  } catch (err) {
    setError(getErrorMessage(err))
    return null
  }
}

/**
 * Runs a QC check for the specified `cliendId`.
 * @param {string} clientId
 * @param {func} setSuccess
 * @param {func} setError
 * @param {func} setLoading
 * @returns `data` results
 */
export const createQcCheck = async (
  id,
  setSuccess = () => {},
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/clients/${id}/qc-check-runs/?expand=client`)
    setSuccess(data)
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Cancels a rules check run.
 * @param {string} id
 * @param {string} taskId
 * @param {func} setSuccess
 * @param {func} setError
 * @param {func} setLoading
 */
export const cancelQcCheckRun = async (
  id,
  taskId,
  setSuccess = () => {},
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.patch(`/clients/${id}/qc-check-runs/${taskId}/`, {
      markAsCanceled: true,
    })

    setSuccess(response)
    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get QC Check run for the specified client by `id`.
 * @param {string} id
 * @param {string} qcCheckId
 * @param {func} setError
 * @param {func} setLoading
 * @returns `data` results
 */
export const getClientQcCheckRun = async (
  id,
  qcCheckId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(
      `/clients/${id}/qc-check-runs/${qcCheckId}?expand=client,qc_check`,
    )

    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get the qc check for the specified client by `id`.
 * @param {string} id
 * @param {string} qcCheckId
 * @param {func} setError
 * @param {func} setLoading
 * @returns `data` results
 */
export const getClientQcCheck = async (
  id,
  qcCheckId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(`/clients/${id}/qc-checks/${qcCheckId}/`)

    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}
/**
 * Get the qc check flags for the specified client and QC Check by `id`.
 * @param {string} url
 * @param {func} setError
 * @param {func} setLoading
 * @returns `data` results
 */
export const getClientQcCheckFlags = async (url, setError = () => {}, setLoading = () => {}) => {
  setLoading(true)

  try {
    const data = await axios.get(url)

    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/** ----------------------------- Exports ------------------------------- */

/**
 * Creates a QC Check export using the specified `qcCheckId`.
 * @param {string} clientId
 * @param {object} qcCheckId
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const createClientQcCheckExport = async (
  clientId,
  qcCheckId,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/clients/${clientId}/qc-check-export/?expand=client`, {
      qcCheck: qcCheckId,
    })

    setLoading(false)
    setSuccess('Export started.')
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets a QC Check export using the specified `exportId`.
 * @param {string} clientId
 * @param {string} exportId
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClientQcCheckExport = async (
  clientId,
  exportId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(`/clients/${clientId}/qc-check-export/${exportId}/?expand=client`)

    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}
